import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { StoreContext } from "shared/context/store-context";
import { AuthContext } from "shared/context/auth-context";
import { isPhoneAppAdminUiEnabled } from "config/ConfigUtils";

import GenericCard from "components/ui/cards/GenericCard";
import Input from "shared/components/FormElements/Input";
import Button from "shared/components/FormElements/Button";
import FormFooter from "shared/components/FormElements/FormFooter";
import CustomSelect from "shared/components/FormElements/Select";
import DatePicker from "shared/components/FormElements/DatePicker";
import MapWithTransferTimesContainer from "components/shared/MapWithTransferTimesContainer";
import MapSinglePoint from "components/shared/MapSinglePoint";
import DeleteModal from "components/shared/DeleteModal";
import Modal from "shared/components/UIElements/Modal";
import AreYouSureModal from "components/shared/AreYouSureModal";

import { VALIDATOR_NONE } from "shared/util/validators";
import { TOUR_ROUTE_TYPE, isValidTourStopTime, COMPANY_FEATURES } from "@routebot/constants";
import { checkIsPickUp } from "shared/utility/types";
import { personelServiceRouteTypes_tr, personelServiceRouteTypes_en, isActiveOptions_tr, isActiveOptions_en } from "shared/util/types";
import { getCurrentLanguage } from "shared/utility/lang";

import { useTourBuilderApi } from "./hooks/useTourBuilderApi";
import TourDriverSms from "./TourDriverSms";

// Tour Builder — authoring form for a single place-to-place tour.
//
// Deliberately dependency-free (no drag lib): stops are reordered with up/down and, when
// pickup times are entered, the SHARED `orderTourStops` rule (identical to mobile) is what
// finally decides driver-facing order. The persisted result is a plain PersonelService.

const scrapeMetric = (elementId, kind) => {
  try {
    const raw = document.getElementById(elementId)?.textContent;
    if (raw === null) return undefined;
    if (kind === "distance") {
      const parsed = parseFloat(String(raw).replace("km", "").trim());
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    const parsed = parseInt(String(raw), 10);
    return Number.isFinite(parsed) ? parsed : undefined;
  } catch (err) {
    return undefined;
  }
};

const pickStopLocation = (place) => {
  if (!place) return null;
  return place.locationgeton || place.location || place.locationgetoff || null;
};

const isUsableLocation = (loc) => {
  if (!loc) return false;
  const lat = Number(loc.lat);
  const lng = Number(loc.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat === -1.1 && lng === -1.1) return false;
  if (lat === 0 && lng === 0) return false;
  return true;
};

const getOppositeRouteType = (routeType) => (checkIsPickUp(routeType) ? "DROP_OFF" : "PICK_UP");

const TourBuilderItem = ({ itemId, onClose, isNewItem, gotoItem, InsertItem, UpdateItem, DeleteItem }) => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const showPhoneAppAdminUi = isPhoneAppAdminUiEnabled(auth?.company?.features);
  const { driverOptions, drivers, personelSchoolOptions, schools } = useContext(StoreContext);
  const { listPlaces, createPlace, updatePlace, deletePlace, getTour, createTour, updateTour } = useTourBuilderApi();

  const smsEnabled = !!auth?.company?.features?.[COMPANY_FEATURES.SMS_COMMUNICATION];

  const keyRef = useRef(1);
  const nextKey = useCallback(() => `stop_${keyRef.current++}`, []);
  const metricsSuffix = `-tour-${itemId || "new"}`;

  const [isReady, setIsReady] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showOppositeModal, setShowOppositeModal] = useState(false);
  const [isCreatingOpposite, setIsCreatingOpposite] = useState(false);

  const [name, setName] = useState("");
  const [driverId, setDriverId] = useState("");
  const [schoolId, setSchoolId] = useState(personelSchoolOptions?.length === 1 ? personelSchoolOptions[0].value : "");
  const [date, setDate] = useState("");
  const [arrivalTime, setArrivalTime] = useState("");
  const [routeType, setRouteType] = useState(TOUR_ROUTE_TYPE); // PICK_UP (default) | DROP_OFF
  const [plate, setPlate] = useState("");
  const [agency, setAgency] = useState("");
  const [personCount, setPersonCount] = useState("");
  const [isActive, setIsActive] = useState(true);

  const isPickUp = checkIsPickUp(routeType);
  const routeTypeOptions = getCurrentLanguage() === "tr" ? personelServiceRouteTypes_tr : personelServiceRouteTypes_en;
  const isActiveOptions = getCurrentLanguage() === "tr" ? isActiveOptions_tr : isActiveOptions_en;

  const [stops, setStops] = useState([]);
  const [places, setPlaces] = useState([]);
  const [placePickerKey, setPlacePickerKey] = useState(0);

  const [savedDistance, setSavedDistance] = useState(undefined);
  const [savedDuration, setSavedDuration] = useState(undefined);

  // Add/Edit-place inline panel state. `editingPlaceId === null` → create mode; otherwise the
  // panel edits that existing POI place (shared across tours).
  const [showAddPlace, setShowAddPlace] = useState(false);
  const [editingPlaceId, setEditingPlaceId] = useState(null);
  const [newPlaceName, setNewPlaceName] = useState("");
  const [newPlaceAddress, setNewPlaceAddress] = useState("");
  const [newPlaceLocation, setNewPlaceLocation] = useState(null);
  const [isCreatingPlace, setIsCreatingPlace] = useState(false);
  const [placePanelKey, setPlacePanelKey] = useState(0); // remounts MapSinglePoint with fresh initials

  // "Yerleri Yönet" (manage places library) modal
  const [showPlaceManager, setShowPlaceManager] = useState(false);
  const [deletingPlaceId, setDeletingPlaceId] = useState(null);

  // --- Load POI library once ---
  useEffect(() => {
    let alive = true;
    (async () => {
      const loaded = await listPlaces();
      if (alive) setPlaces(Array.isArray(loaded) ? loaded : []);
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Load existing tour for edit ---
  useEffect(() => {
    let alive = true;
    (async () => {
      if (isNewItem || !itemId) {
        setIsReady(true);
        return;
      }
      const tour = await getTour(itemId);
      if (!alive) return;
      if (tour) {
        setName(tour.name || "");
        setDriverId(tour.drivers?.[0]?.id || tour.drivers?.[0] || "");
        setSchoolId(tour.school?.id || tour.school || "");
        setDate(tour.date || "");
        setArrivalTime(isValidTourStopTime(tour.routeClientArrivalTime) ? tour.routeClientArrivalTime : "");
        setRouteType(checkIsPickUp(tour.routeType) ? "PICK_UP" : "DROP_OFF");
        setPlate(tour.plate || "");
        setAgency(tour.agency || "");
        setPersonCount(typeof tour.personCount === "number" ? String(tour.personCount) : "");
        setIsActive(tour.isActive !== false);
        setSavedDistance(tour.totalDistance);
        setSavedDuration(tour.totalDuration);

        const timeByUser = new Map((tour.usersdetails || []).map((d) => [String(d?.user), d?.transfertime]));
        const rebuiltStops = (tour.users || []).map((user) => ({
          key: nextKey(),
          placeId: user.id || String(user),
          name: user.name || "",
          location: pickStopLocation(user),
          transfertime: isValidTourStopTime(timeByUser.get(String(user.id))) ? timeByUser.get(String(user.id)) : "",
        }));
        setStops(rebuiltStops);
      }
      setIsReady(true);
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, isNewItem]);

  const origin = useMemo(() => schools.find((s) => s.id === schoolId) || null, [schools, schoolId]);

  // SINGLE source of truth for the whole page = the `stops` array order. The list above, the
  // map preview and the save payload ALL use this exact order. So reordering (up/down here, or
  // drag on the map via onOptimize) stays in sync and never snaps back. Per-stop times are
  // annotations only — they no longer re-sort the UI. (Mobile still sorts by transfertime.)
  const mapStops = useMemo(
    () =>
      stops.map((s) => ({
        id: s.key,
        name: s.name,
        location: s.location,
        transferTime: s.transfertime, // map component reads camelCase
      })),
    [stops],
  );

  const placeOptions = useMemo(() => places.map((p) => ({ value: p.id, label: p.name })), [places]);

  // --- Stop operations ---
  const addStopFromPlace = useCallback(
    (placeId) => {
      const place = places.find((p) => p.id === placeId);
      if (!place) return;
      setStops((prev) => [
        ...prev,
        {
          key: nextKey(),
          placeId: place.id,
          name: place.name,
          location: pickStopLocation(place),
          transfertime: "",
        },
      ]);
      setPlacePickerKey((k) => k + 1); // reset picker selection
    },
    [places, nextKey],
  );

  const removeStop = useCallback((key) => {
    setStops((prev) => prev.filter((s) => s.key !== key));
  }, []);

  const handleOptimize = useCallback((newStops) => {
    // The map panel is the single place to reorder stops AND edit pickup times; both flow back
    // here via onOptimize. We merge the new ORDER and the updated `transferTime` (camelCase →
    // our `transfertime`) into our `stops`.
    //
    // IMPORTANT: always return a NEW array reference (even if content is identical). The shared
    // map clears its "loading" state of the distance/duration ONLY after the `stops` prop changes
    // (it deliberately skips setLoading(false) and relies on the re-render). If we returned the
    // same reference on a no-op optimize (e.g. already-optimal order + no arrival time → no time
    // change), the map would stay stuck in "loading". The map only emits onOptimize on user
    // actions (optimize / up-down / time edit), not on every render, so a fresh reference here
    // does NOT cause a render loop.
    setStops((prev) => {
      const byKey = new Map(prev.map((s) => [s.key, s]));
      const next = newStops
        .map((ns) => {
          const orig = byKey.get(ns.id);
          if (!orig) return null;
          const nextTime = typeof ns.transferTime === "string" ? ns.transferTime : orig.transfertime;
          return nextTime === orig.transfertime ? orig : { ...orig, transfertime: nextTime };
        })
        .filter(Boolean);
      // Defensive: if the map returned an unexpected set (length mismatch), keep our stops.
      return next.length === prev.length ? next : prev;
    });
  }, []);

  // --- Add new place (map pin) ---
  const handlePlaceMapData = useCallback((address, location) => {
    setNewPlaceAddress(address || "");
    setNewPlaceLocation(isUsableLocation(location) ? location : null);
  }, []);

  const resetAddPlacePanel = useCallback(() => {
    setShowAddPlace(false);
    setEditingPlaceId(null);
    setNewPlaceName("");
    setNewPlaceAddress("");
    setNewPlaceLocation(null);
  }, []);

  // Open the panel in CREATE mode (blank).
  const beginAddPlace = useCallback(() => {
    setEditingPlaceId(null);
    setNewPlaceName("");
    setNewPlaceAddress("");
    setNewPlaceLocation(null);
    setPlacePanelKey((k) => k + 1);
    setShowAddPlace(true);
  }, []);

  // Open the panel in EDIT mode, prefilled from an existing place (accepts a stop or a library
  // place shape). Editing changes the shared POI for every tour that uses it.
  const beginEditPlace = useCallback(
    (source) => {
      const placeId = source.placeId || source.id;
      const libraryPlace = places.find((p) => p.id === placeId);
      setEditingPlaceId(placeId);
      setNewPlaceName(source.name || libraryPlace?.name || "");
      setNewPlaceAddress(libraryPlace?.address || "");
      setNewPlaceLocation(source.location || libraryPlace?.locationgeton || libraryPlace?.location || null);
      setPlacePanelKey((k) => k + 1);
      setShowPlaceManager(false);
      setShowAddPlace(true);
    },
    [places],
  );

  const handleSavePlace = useCallback(async () => {
    if (!newPlaceName.trim()) {
      window.toast?.error(t("tourBuilder.placeNameRequired"));
      return;
    }
    if (!isUsableLocation(newPlaceLocation)) {
      window.toast?.error(t("tourBuilder.placeLocationRequired"));
      return;
    }
    setIsCreatingPlace(true);
    const payload = {
      name: newPlaceName.trim(),
      location: newPlaceLocation,
      address: newPlaceAddress,
    };

    if (editingPlaceId) {
      // EDIT existing place → update library + every stop that references it.
      const place = await updatePlace(editingPlaceId, payload);
      setIsCreatingPlace(false);
      if (place) {
        setPlaces((prev) => prev.map((p) => (p.id === place.id ? place : p)));
        setStops((prev) =>
          prev.map((s) =>
            s.placeId === place.id
              ? { ...s, name: place.name, location: place.locationgeton || place.location || s.location }
              : s,
          ),
        );
        window.toast?.success(t("tourBuilder.placeUpdated"));
        resetAddPlacePanel();
      }
      return;
    }

    // CREATE new place → add to library + add as a stop.
    const place = await createPlace(payload);
    setIsCreatingPlace(false);
    if (place) {
      setPlaces((prev) => {
        const exists = prev.some((p) => p.id === place.id);
        return exists ? prev.map((p) => (p.id === place.id ? place : p)) : [...prev, place];
      });
      addStopFromPlace(place.id);
      resetAddPlacePanel();
    }
  }, [
    newPlaceName,
    newPlaceLocation,
    newPlaceAddress,
    editingPlaceId,
    updatePlace,
    createPlace,
    addStopFromPlace,
    resetAddPlacePanel,
    t,
  ]);

  // Delete a place from the DB (library manager). Blocked server-side if it's used by any tour.
  const handleDeletePlace = useCallback(
    async (placeId) => {
      setDeletingPlaceId(placeId);
      const result = await deletePlace(placeId);
      setDeletingPlaceId(null);
      if (!result) return;
      if (result.deleted) {
        setPlaces((prev) => prev.filter((p) => p.id !== placeId));
        window.toast?.success(t("tourBuilder.placeDeleted"));
      } else {
        const names = (result.tours || []).map((tr) => tr.name).filter(Boolean).join(", ");
        window.toast?.error(t("tourBuilder.placeDeleteBlocked", { tours: names }));
      }
    },
    [deletePlace, t],
  );

  // --- Validation ---
  const validationMessage = useMemo(() => {
    if (!driverId) return t("tourBuilder.validation.driver");
    if (!schoolId) return t("tourBuilder.validation.destination");
    if (!date) return t("tourBuilder.validation.date");
    if (stops.length === 0) return t("tourBuilder.validation.stops");
    if (stops.some((s) => !isUsableLocation(s.location))) return t("tourBuilder.validation.stopLocation");
    if (!origin || !isUsableLocation(origin.location)) return t("tourBuilder.validation.destinationLocation");
    return null;
  }, [driverId, schoolId, date, stops, origin, t]);

  // --- Save ---
  const handleSave = useCallback(async () => {
    if (validationMessage) {
      window.toast?.error(validationMessage);
      return;
    }
    setIsSaving(true);

    const totalDistance = scrapeMetric(`totalDistance${metricsSuffix}`, "distance");
    const totalDuration = scrapeMetric(`totalDuration${metricsSuffix}`, "duration");

    const payload = {
      name: name.trim() || undefined,
      driver: driverId,
      school: schoolId,
      date,
      routeType,
      // Always sent (even empty) so that on EDIT the operator can clear them; backend normalizes
      // empty → unset/null.
      plate: plate.trim(),
      agency: agency.trim(),
      personCount,
      isActive,
      routeClientArrivalTime: isValidTourStopTime(arrivalTime) ? arrivalTime : undefined,
      stops: stops.map((s) => ({
        placeId: s.placeId,
        name: s.name,
        location: s.location,
        transfertime: isValidTourStopTime(s.transfertime) ? s.transfertime : undefined,
      })),
      ...(totalDistance !== undefined ? { totalDistance } : {}),
      ...(totalDuration !== undefined ? { totalDuration } : {}),
    };

    const saved = isNewItem ? await createTour(payload) : await updateTour(itemId, payload);
    setIsSaving(false);

    if (saved) {
      if (isNewItem) {
        InsertItem?.(saved);
        window.toast?.success(t("tourBuilder.createdSuccess"));
        if (saved.id && gotoItem) {
          gotoItem(saved.id);
        } else {
          onClose?.();
        }
      } else {
        UpdateItem?.(saved);
        window.toast?.success(t("updateSuccess"));
      }
    }
  }, [
    validationMessage,
    metricsSuffix,
    name,
    driverId,
    schoolId,
    date,
    arrivalTime,
    routeType,
    plate,
    agency,
    personCount,
    isActive,
    stops,
    isNewItem,
    createTour,
    updateTour,
    itemId,
    InsertItem,
    UpdateItem,
    gotoItem,
    onClose,
    t,
  ]);

  // Selected driver (with phone) for the SMS card.
  const selectedDriver = (drivers || []).find((d) => String(d.id) === String(driverId)) || null;

  // "Ters servis yarat" — creates a NEW tour with the opposite direction (PICK_UP↔DROP_OFF),
  // same anchor + stops + vehicle/agency/person info. Per-stop pickup times and the arrival time
  // are intentionally NOT copied (they'd be wrong for the opposite direction), mirroring the
  // standard services' opposite-service behavior.
  const handleCreateOpposite = useCallback(async () => {
    const opposite = getOppositeRouteType(routeType);
    const payload = {
      name: name.trim() || undefined,
      driver: driverId,
      school: schoolId,
      date,
      routeType: opposite,
      plate: plate.trim(),
      agency: agency.trim(),
      personCount,
      routeClientArrivalTime: undefined,
      stops: stops.map((s) => ({ placeId: s.placeId, name: s.name, location: s.location })),
    };

    setIsCreatingOpposite(true);
    const saved = await createTour(payload);
    setIsCreatingOpposite(false);

    if (saved) {
      InsertItem?.(saved);
      window.toast?.success(t("oppositeServiceCreated"));
      if (saved.id && gotoItem) gotoItem(saved.id);
    }
  }, [routeType, name, driverId, schoolId, date, plate, agency, personCount, stops, createTour, InsertItem, gotoItem, t]);

  if (personelSchoolOptions?.length === 0) {
    return <div style={{ padding: 20 }}>{t("tourBuilder.noDestinationSchool")}</div>;
  }

  if (!isReady) {
    return <div style={{ padding: 20 }}>{t("Loading")}</div>;
  }

  return (
    <div className="vertical-flex" style={{ display: "flex", flexDirection: "column", rowGap: 20 }}>
      {!isNewItem && itemId && (
        <DeleteModal
          endPoint="personelservices"
          itemType="personelservice"
          itemId={itemId}
          onDeleted={() => {
            DeleteItem?.(itemId);
            onClose?.();
          }}
          onClose={() => setShowDeleteModal(false)}
          showModal={showDeleteModal}
        />
      )}

      {/* Ters servis (opposite direction) onayı */}
      <AreYouSureModal
        showModal={showOppositeModal}
        text={t("createOppositeServiceConfirm", { type: isPickUp ? t("dropOff") : t("pickUp") })}
        onClose={() => setShowOppositeModal(false)}
        onConfirmed={handleCreateOpposite}
      />

      {/* Yerleri Yönet — POI library manager (edit / delete, delete blocked if used by a tour) */}
      <Modal
        show={showPlaceManager}
        header={t("tourBuilder.managePlaces")}
        onCancel={() => setShowPlaceManager(false)}
        onClose={() => setShowPlaceManager(false)}
        style={{ width: "90%", maxWidth: 560 }}
        footer={
          <Button type="button" onClick={() => setShowPlaceManager(false)}>
            {t("close")}
          </Button>
        }
      >
        {places.length === 0 ? (
          <div style={{ color: "#999", padding: 12 }}>{t("tourBuilder.noPlaces")}</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 440, overflowY: "auto" }}>
            {places.map((p) => (
              <div
                key={p.id}
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  padding: "8px 10px",
                  border: "1px solid #eee",
                  borderRadius: 6,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.name}
                  </div>
                  {p.address && (
                    <div
                      style={{
                        fontSize: 12,
                        color: "#888",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {p.address}
                    </div>
                  )}
                </div>
                <Button type="button" size="small" onClick={() => beginEditPlace(p)}>
                  {t("tourBuilder.editPlace")}
                </Button>
                <Button
                  type="button"
                  size="small"
                  danger
                  onClick={() => handleDeletePlace(p.id)}
                  disabled={deletingPlaceId === p.id}
                >
                  {t("delete")}
                </Button>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* SECTION 1 — Tur Bilgileri: single row via horizontal-flex; wider weight for the first three */}
      <GenericCard title={t("tourBuilder.tourInfo")} dismissible={false} style={{ paddingBottom: 0 }}>
        {/* Row 1 — direction + who/where + name */}
        <div className="horizontal-flex" style={{ columnGap: 12, justifyContent: "flex-start" }}>
          <div style={{ flex: 2, minWidth: 0 }}>
            <CustomSelect
              options={routeTypeOptions}
              id="tourRouteType"
              onInput={(_id, value) => setRouteType(value)}
              label={t("routeType")}
              description={t("tourBuilder.routeTypeDescription")}
              initialValue={routeTypeOptions.find((o) => o.value === routeType) || routeTypeOptions[0]}
              initialValid={true}
            />
          </div>
          <div style={{ flex: 2, minWidth: 0 }}>
            <CustomSelect
              options={driverOptions}
              id="tourDriver"
              onInput={(_id, value) => setDriverId(value)}
              label={t("tourBuilder.driver") + " *"}
              initialValue={driverOptions.find((o) => o.value === driverId) || null}
              initialValid={true}
            />
          </div>
          <div style={{ flex: 2, minWidth: 0 }}>
            <CustomSelect
              options={personelSchoolOptions}
              id="tourSchool"
              onInput={(_id, value) => setSchoolId(value)}
              label={(isPickUp ? t("destination") : t("origin")) + " *"}
              description={isPickUp ? t("tourBuilder.destinationDescription") : t("tourBuilder.originDescription")}
              initialValue={personelSchoolOptions.find((o) => o.value === schoolId) || null}
              initialValid={true}
            />
          </div>
          <div style={{ flex: 3, minWidth: 0 }}>
            <Input
              id="tourName"
              element="input"
              type="text"
              label={t("tourBuilder.name")}
              validators={[VALIDATOR_NONE()]}
              initialValue={name}
              initialValid={true}
              onInput={(_id, value) => setName(value)}
              description={t("tourBuilder.nameDescription")}
            />
          </div>
        </div>

        {/* Row 2 — date + time + vehicle/agency/person count */}
        <div className="horizontal-flex" style={{ columnGap: 12, justifyContent: "flex-start" }}>
          <div style={{ flex: "0 0 160px" }}>
            <DatePicker
              id="tourDate"
              onInput={(_id, value) => setDate(value)}
              label={t("tourBuilder.date") + " *"}
              initialValue={date || ""}
              initialValid={true}
            />
          </div>
          <div style={{ flex: "0 0 150px" }}>
            <Input
              id="tourArrival"
              element="input"
              type="text"
              label={isPickUp ? t("pickUpRouteClientArrivalTime") : t("dropOffRouteClientArrivalTime")}
              placeholder="HH:mm"
              validators={[VALIDATOR_NONE()]}
              initialValue={arrivalTime}
              initialValid={true}
              onInput={(_id, value) => setArrivalTime(value)}
            />
          </div>
          <div style={{ flex: "0 0 150px" }}>
            <Input
              id="tourPlate"
              element="input"
              type="text"
              label={t("plate")}
              validators={[VALIDATOR_NONE()]}
              initialValue={plate}
              initialValid={true}
              onInput={(_id, value) => setPlate(value)}
            />
          </div>
          <div style={{ flex: 2, minWidth: 0 }}>
            <Input
              id="tourAgency"
              element="input"
              type="text"
              label={t("tourBuilder.agency")}
              validators={[VALIDATOR_NONE()]}
              initialValue={agency}
              initialValid={true}
              onInput={(_id, value) => setAgency(value)}
              description={t("tourBuilder.agencyDescription")}
            />
          </div>
          <div style={{ flex: "0 0 130px" }}>
            <Input
              id="tourPersonCount"
              element="input"
              type="number"
              label={t("tourBuilder.personCount")}
              validators={[VALIDATOR_NONE()]}
              initialValue={personCount}
              initialValid={true}
              onInput={(_id, value) => setPersonCount(value)}
            />
          </div>
          {showPhoneAppAdminUi && (
            <div style={{ flex: "0 0 150px" }}>
              <CustomSelect
                options={isActiveOptions}
                id="tourIsActive"
                onInput={(_id, value) => setIsActive(value)}
                label={t("routeStatus")}
                description={t("routeStatusDescription")}
                initialValue={isActive ? isActiveOptions[1] : isActiveOptions[0]}
                initialValid={true}
              />
            </div>
          )}
        </div>
      </GenericCard>

      {/* SECTIONS 2+3 — Duraklar (left) + Sürücüye Bildirim (right), side by side */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }}>
        <div style={{ flex: "1 1 480px", minWidth: 340 }}>
          <GenericCard title={t("tourBuilder.stops")} style={{ paddingBottom: 0 }}>
            <div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
                <div style={{ flex: "1 1 260px", minWidth: 220 }}>
                  <CustomSelect
                    key={`place-picker-${placePickerKey}`}
                    options={placeOptions}
                    id="tourAddPlace"
                    onInput={() => {}}
                    fireChange={(value) => addStopFromPlace(value)}
                    label={t("tourBuilder.addFromLibrary")}
                    initialValue={null}
                    initialValid={true}
                  />
                </div>
                <div style={{ marginBottom: 18, display: "flex", gap: 8 }}>
                  <Button
                    type="button"
                    size="small"
                    onClick={() => (showAddPlace && !editingPlaceId ? resetAddPlacePanel() : beginAddPlace())}
                  >
                    {showAddPlace && !editingPlaceId ? t("tourBuilder.cancelAddPlace") : t("tourBuilder.addFromMap")}
                  </Button>
                  <Button type="button" size="small" onClick={() => setShowPlaceManager(true)}>
                    {t("tourBuilder.managePlaces")}
                  </Button>
                </div>
              </div>

              {showAddPlace && (
                <div
                  style={{
                    border: "1px solid #eee",
                    borderRadius: 6,
                    padding: 12,
                    marginBottom: 16,
                    backgroundColor: "#fafafa",
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>
                    {editingPlaceId ? t("tourBuilder.editPlaceTitle") : t("tourBuilder.addPlaceTitle")}
                  </div>
                  <Input
                    key={`newPlaceName-${placePanelKey}`}
                    id="newPlaceName"
                    element="input"
                    type="text"
                    label={t("tourBuilder.placeName") + " *"}
                    validators={[VALIDATOR_NONE()]}
                    initialValue={newPlaceName}
                    initialValid={true}
                    onInput={(_id, value) => setNewPlaceName(value)}
                  />
                  <MapSinglePoint
                    key={`newPlaceLocation-${placePanelKey}`}
                    id="newPlaceLocation"
                    title={t("tourBuilder.placeAddress") + " *"}
                    initialAddress={newPlaceAddress}
                    initialLocation={newPlaceLocation}
                    onMapDataChange={handlePlaceMapData}
                    isRequired={true}
                  />
                  <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                    <Button type="button" onClick={handleSavePlace} disabled={isCreatingPlace}>
                      {editingPlaceId ? t("tourBuilder.saveChanges") : t("tourBuilder.saveAndAddPlace")}
                    </Button>
                    <Button type="button" danger onClick={resetAddPlacePanel} disabled={isCreatingPlace}>
                      {t("cancel")}
                    </Button>
                  </div>
                </div>
              )}

              <div style={{ fontSize: 12, color: "#888", margin: "4px 0 8px" }}>{t("tourBuilder.orderHint")}</div>

              {stops.length === 0 ? (
                <div style={{ color: "#999", padding: "8px 0" }}>{t("tourBuilder.noStops")}</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {stops.map((stop, index) => (
                    <div
                      key={stop.key}
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                        padding: "6px 10px",
                        border: "1px solid #eee",
                        borderRadius: 6,
                        backgroundColor: "#fff",
                      }}
                    >
                      <span style={{ fontWeight: 700, minWidth: 20, color: "#555" }}>{index + 1}.</span>
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={stop.name}
                      >
                        {stop.name}
                      </span>
                      {isValidTourStopTime(stop.transfertime) && (
                        <span style={{ fontSize: 12, color: "#888", minWidth: 44, textAlign: "right" }}>
                          {stop.transfertime}
                        </span>
                      )}
                      <Button
                        type="button"
                        size="small"
                        onClick={() => beginEditPlace(stop)}
                        title={t("tourBuilder.editPlace")}
                      >
                        ✎
                      </Button>
                      <Button
                        type="button"
                        size="small"
                        danger
                        onClick={() => removeStop(stop.key)}
                        title={t("tourBuilder.removeFromTour")}
                      >
                        ✕
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </GenericCard>
        </div>

        <div style={{ flex: "1 1 320px", minWidth: 300 }}>
          {smsEnabled && (
            <GenericCard title={t("tourBuilder.notifyCardTitle")} style={{ paddingBottom: 0 }}>
              <TourDriverSms
                driver={selectedDriver}
                serviceId={isNewItem ? null : itemId}
                defaultMessage={t("tourBuilder.defaultDriverMessage")}
              />
            </GenericCard>
          )}
        </div>
      </div>

      {/* SECTION 4 — Rota Önizleme (full width; map needs the space) */}
      <GenericCard title={t("tourBuilder.routePreview")} style={{ paddingBottom: 0 }}>
        {origin && stops.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "row", columnGap: 10 }}>
            <MapWithTransferTimesContainer
              stops={mapStops}
              origin={origin}
              type={routeType}
              enablePolyline={true}
              transferStartFinishTime={isValidTourStopTime(arrivalTime) ? arrivalTime : null}
              idSuffix={metricsSuffix}
              savedTotalDistanceKm={savedDistance}
              savedTotalDurationMin={savedDuration}
              onOptimize={handleOptimize}
            />
          </div>
        ) : (
          <div style={{ padding: "32px 12px", color: "#999", textAlign: "center" }}>
            {t("tourBuilder.mapPlaceholder")}
          </div>
        )}
      </GenericCard>

      {/* Standard sticky form footer (Update + Delete). No cancel — the panel has a top "Close". */}
      <FormFooter
        isNewItem={isNewItem}
        onUpdate={handleSave}
        onDelete={() => setShowDeleteModal(true)}
        isUpdating={isSaving}
        disabled={isSaving || !!validationMessage}
        cta={isNewItem ? t("tourBuilder.createTour") : null}
        extraActions={
          !isNewItem && itemId ? (
            <Button
              type="button"
              size="small"
              onClick={() => setShowOppositeModal(true)}
              disabled={isSaving || isCreatingOpposite || !!validationMessage}
            >
              {isPickUp ? t("createDropOffVersion") : t("createPickUpVersion")}
            </Button>
          ) : null
        }
      />
    </div>
  );
};

export default TourBuilderItem;
